import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardHeader, CardBody, CardTitle, Button, Form, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './VPNDigitalForm.css';
import { FiPlus, FiMinus, FiCheck, FiShield, FiUser,FiLock, FiInfo, FiAlertTriangle, FiAlertCircle, FiX, FiSend, FiCalendar, FiUsers } from 'react-icons/fi';
import { FaBuildingColumns } from 'react-icons/fa6';

const VPNDigitalForm = () => {
  const [searchParams] = useSearchParams();
  const renewalId = searchParams.get('renewalId');
  
  // Form state
  const [formData, setFormData] = useState({
    requestType: 'VPN Access',
    companyName: '',
    contactPersonName: '',
    email: '',
    phoneNumber: '',
    reasonForAccess: '',
    startDate: '',
    endDate: '',
    people: [{ name: '', fullName: '', email: '' }],
    description: '',
    securityCompliance: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [duration, setDuration] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRenewalForm, setIsRenewalForm] = useState(false);
  const [expirationDate, setExpirationDate] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [renewalNotFound, setRenewalNotFound] = useState(false);
  const [useUntilCutoff, setUseUntilCutoff] = useState(true);

  // Helper function to get today's date in YYYY-MM-DD format (timezone-safe)
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate max end date based on today's date (helper function)
  const getMaxEndDateBasedOnToday = () => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();
    
    // If today is after October (i.e., November or December), allow selection until next year's October 31st
    // Otherwise, allow selection until this year's October 31st
    let targetYear = todayYear;
    if (todayMonth > 9) {
      // If we're in November (month 10) or December (month 11)
      targetYear = todayYear + 1;
    }
    
    // Return formatted date string directly to avoid timezone issues
    return `${targetYear}-10-31`;
  };

  // Calculate duration between dates
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) {
      setDuration(null);
      return;
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Calculate difference
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;
    
    setDuration({ months, days, totalDays: diffDays });
  };

  // Helper: format datetime in Malaysia time
  const formatMalaysiaDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      return new Intl.DateTimeFormat('en-MY', {
        timeZone: 'Asia/Kuala_Lumpur',
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(new Date(isoString));
    } catch {
      return isoString;
    }
  };

  // Load vendor data if renewal form (renewalId present)
  useEffect(() => {
    if (renewalId) {
      setIsRenewalForm(true);
      setIsLoading(true);
      
      const serverUrl = process.env.REACT_APP_AI_FORM_SERVER || 'http://localhost:4454';
      fetch(`${serverUrl}/api/vpn-vendors/renewal/${renewalId}`)
        .then(response => {
          // Check if response is not ok (404, 500, etc.)
          if (!response.ok) {
            return response.json().then(data => {
              // If renewal not found or invalid, set renewalNotFound flag
              setRenewalNotFound(true);
              return null;
            });
          }
          return response.json();
        })
        .then(data => {
          // If data is null (from error handling above), return early
          if (!data) {
            return;
          }
          
          // Check if success is false or vendor data is missing
          if (!data.success || !data.vendor) {
            setRenewalNotFound(true);
            return;
          }
          
          const vendor = data.vendor;
          const renewalRecord = data.renewalRecord;
          
          // Check if vendor data is actually empty (company name and PIC are empty)
          if ((!vendor.CompanyName || vendor.CompanyName.trim() === '') && 
              (!renewalRecord?.VendorPICAtSubmission?.Name || renewalRecord.VendorPICAtSubmission.Name.trim() === '')) {
            setRenewalNotFound(true);
            return;
          }
          
          // Set expiration date
          const expDate = data.expirationDate || renewalRecord?.ExpirationDate;
          if (expDate) {
            setExpirationDate(expDate);
          }
          
          // Check if expired (check both backend flag and status)
          const isExpiredFromBackend = data.expired || renewalRecord?.Status === 'expired';
          
          // Also check client-side expiration as fallback
          let isExpiredClientSide = false;
          if (expDate) {
            const expirationTime = new Date(expDate);
            const now = new Date();
            isExpiredClientSide = now > expirationTime;
          }
          
          const formIsExpired = isExpiredFromBackend || isExpiredClientSide;
          
          if (formIsExpired) {
            setIsExpired(true);
            // Don't pre-fill form data if expired
            return;
          }
          
          // If already submitted, show success message
          if (data.submitted) {
            setSubmitSuccess(true);
            return;
          }
          
          // Pre-fill people list from renewalRecord.PersonList (if exists)
          let peopleList = [{ name: '', fullName: '', email: '' }];
          if (renewalRecord.PersonList && Array.isArray(renewalRecord.PersonList) && renewalRecord.PersonList.length > 0) {
            // Convert PersonList format to form people format
            peopleList = renewalRecord.PersonList.map(person => ({
              name: person.Name || person.name || '',
              fullName: person.FullName || person.fullName || '',
              email: person.Email || person.email || ''
            }));
          }
          
          // Set default dates
          const todayStr = getTodayDateString();
          const maxEndDateStr = getMaxEndDateBasedOnToday();
          
          // Pre-fill form with vendor data only if not expired
          setFormData({
            requestType: 'VPN Access',
            companyName: vendor.CompanyName || '',
            contactPersonName: renewalRecord?.VendorPICAtSubmission?.Name || '',
            email: renewalRecord?.VendorPICAtSubmission?.Email || '',
            phoneNumber: renewalRecord?.VendorPICAtSubmission?.Phone || '',
            reasonForAccess: renewalRecord?.Reason || '',
            startDate: todayStr,
            endDate: maxEndDateStr,
            people: peopleList,
            description: '',
            securityCompliance: false
          });
          
          // Calculate initial duration
          calculateDuration(todayStr, maxEndDateStr);
        })
        .catch(error => {
          // On any error, show the not found message
          setRenewalNotFound(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // For new forms (not renewal), set default dates
      const todayStr = getTodayDateString();
      const maxEndDateStr = getMaxEndDateBasedOnToday();
      
      setFormData(prev => ({
        ...prev,
        startDate: todayStr,
        endDate: maxEndDateStr
      }));
      
      // Calculate initial duration
      calculateDuration(todayStr, maxEndDateStr);
    }
  }, [renewalId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear related errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Handle date changes and calculate duration
    if (name === 'startDate' || name === 'endDate') {
      const startDate = name === 'startDate' ? value : formData.startDate;
      const endDate = name === 'endDate' ? value : formData.endDate;
      
      calculateDuration(startDate, endDate);
      
      // If start date changes and useUntilCutoff is checked, update end date to max
      if (name === 'startDate' && useUntilCutoff) {
        const maxEndDate = getMaxEndDate(value);
        setFormData(prev => ({
          ...prev,
          endDate: maxEndDate
        }));
        calculateDuration(value, maxEndDate);
      } else if (name === 'startDate' && formData.endDate) {
        // If useUntilCutoff is not checked, still validate the end date
        const maxEndDate = getMaxEndDate(value);
        if (formData.endDate > maxEndDate) {
          setFormData(prev => ({
            ...prev,
            endDate: ''
          }));
          setDuration(null);
        }
      }
    }
  };

  // Handle checkbox for "Until End of next cut off date"
  const handleUntilCutoffChange = (e) => {
    const isChecked = e.target.checked;
    setUseUntilCutoff(isChecked);
    
    if (isChecked) {
      // Reset to today and max cut-off date when checked
      const todayStr = getTodayDateString();
      const maxEndDate = getMaxEndDateBasedOnToday();
      setFormData(prev => ({
        ...prev,
        startDate: todayStr,
        endDate: maxEndDate
      }));
      calculateDuration(todayStr, maxEndDate);
    }
  };

  // Calculate max end date based on today's date
  const getMaxEndDate = (startDate) => {
    if (!startDate) return '';
    return getMaxEndDateBasedOnToday();
  };

  // Handle people changes
  const handlePeopleChange = (index, field, value) => {
    const newPeople = [...formData.people];
    newPeople[index] = {
      ...newPeople[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      people: newPeople
    }));

    // Clear related errors when user starts typing
    if (errors.people) {
      setErrors(prev => ({
        ...prev,
        people: ''
      }));
    }
  };

  // Add person field
  const handleAddPerson = () => {
    if (formData.people.length < 30) {
      setFormData(prev => ({
        ...prev,
        people: [...prev.people, { name: '', fullName: '', email: '' }]
      }));
    }
  };

  // Remove person field
  const handleRemovePerson = (index) => {
    if (formData.people.length > 1) {
      const newPeople = formData.people.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        people: newPeople
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Company Name is mandatory
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    // Contact Person Name is mandatory
    if (!formData.contactPersonName.trim()) {
      newErrors.contactPersonName = 'Contact person name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone Number validation (optional, but validate format if provided)
    if (formData.phoneNumber.trim() && !/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Reason for Access is mandatory
    if (!formData.reasonForAccess.trim()) {
      newErrors.reasonForAccess = 'Reason for access is required';
    }

    // Start Date is mandatory
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    // End Date is mandatory
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Validate end date is not beyond maximum allowed date
    if (formData.startDate && formData.endDate) {
      const maxEndDate = getMaxEndDate(formData.startDate);
      if (formData.endDate > maxEndDate) {
        newErrors.endDate = 'End date cannot exceed the maximum allowed date based on your start date';
      }
      
      // Validate end date is after start date
      if (formData.endDate <= formData.startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    // People validation - at least one person required with fullName and email
    const validPeople = formData.people.filter(person => person.fullName.trim() !== '' && person.email.trim() !== '');
    if (validPeople.length === 0) {
      newErrors.people = 'At least one person with full name and email is required';
    }

    // Validate each person's email format
    formData.people.forEach((person, index) => {
      if (person.fullName.trim() !== '' || person.email.trim() !== '') {
        if (!person.fullName.trim()) {
          newErrors[`people_fullName_${index}`] = 'Full Name is required';
        }
        if (!person.email.trim()) {
          newErrors[`people_email_${index}`] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email)) {
          newErrors[`people_email_${index}`] = 'Please enter a valid email address';
        }
      }
    });

    // Security Compliance is mandatory
    if (!formData.securityCompliance) {
      newErrors.securityCompliance = 'You must confirm that you have read and understand the VPN security policies';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if form has expired
    if (isExpired) {
      setErrors({ submit: 'This form has expired. Please contact IT Support to request a new renewal link.' });
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Format data for submission
      // Transform people array to PersonList format
      const personList = formData.people
        .filter(person => person.fullName.trim() !== '' && person.email.trim() !== '')
        .map(person => ({
          Name: person.name || '',
          FullName: person.fullName,
          Email: person.email,
          VPNAccess: true
        }));
      
      const submissionData = {
        requestType: formData.requestType,
        companyName: formData.companyName,
        contactPersonName: formData.contactPersonName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        reasonForAccess: formData.reasonForAccess,
        startDate: formData.startDate,
        endDate: formData.endDate,
        people: personList,
        description: formData.description,
        securityCompliance: formData.securityCompliance,
        submittedAt: new Date().toISOString()
      };

      // If it's a renewal form (renewalId present), submit to renewal endpoint
      if (renewalId) {
        const serverUrl = process.env.REACT_APP_AI_FORM_SERVER || 'http://localhost:4454';
        const response = await fetch(`${serverUrl}/api/vpn-vendors/renewal/${renewalId}/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(submissionData)
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          if (data.expired) {
            setIsExpired(true);
            throw { expired: true, message: 'This form has expired. Please contact IT Support to request a new renewal link.' };
          }
          throw new Error(data.error || 'Failed to submit renewal form');
        }
      } else {
        // Regular form submission (to be implemented later)
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      setSubmitSuccess(true);
      
    } catch (error) {
      if (error.expired) {
        setIsExpired(true);
        setErrors({ submit: error.message || 'This form has expired. Please contact IT Support to request a new renewal link.' });
      } else {
        setErrors({ submit: error.message || 'Failed to submit request. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while fetching vendor data
  if (isLoading) {
    return (
      <div className="vpn-form-container">
        <div className="vpn-form-wrapper">
          <Card className="text-center">
            <CardBody className="py-5">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5>Loading form...</h5>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Show not found message if renewal ID is invalid or data cannot be retrieved
  if (isRenewalForm && renewalNotFound && !isLoading) {
    return (
      <div className="vpn-form-container">
        <div className="vpn-form-wrapper">
          <Card className="text-center">
            <CardBody className="py-5">
              <div className="avatar avatar-lg bg-warning text-white rounded-circle mb-3 mx-auto">
                <FiAlertCircle />
              </div>
              <h4 className="text-warning mb-3">Renewal Link Not Found</h4>
              <p className="text-muted mb-4">
                The renewal link you are trying to access is invalid or does not exist.
              </p>
              <p className="text-muted mb-4">
                For any assistance, please contact IT Support at <strong>ihp-infra@islandhospital.com</strong>
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // Show expired message if form is expired
  if (isRenewalForm && isExpired && !isLoading) {
    return (
      <div className="vpn-form-container">
        <div className="vpn-form-wrapper">
          <Card className="text-center">
            <CardBody className="py-5">
              <div className="avatar avatar-lg bg-danger text-white rounded-circle mb-3 mx-auto">
                <FiX />
              </div>
              <h4 className="text-danger mb-3">Form Expired</h4>
              <p className="text-muted mb-4">
                This renewal form has expired and is no longer available for submission.
              </p>
              <p className="text-muted mb-4">
                Please contact IT Support at <strong>ihp-infra@islandhospital.com</strong> to request a new renewal link.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="vpn-form-container">
        <div className="vpn-form-wrapper">
          <Card className="text-center">
            <CardBody className="py-5">
              <div className="avatar avatar-lg bg-success text-white rounded-circle mb-3 mx-auto">
                <FiCheck />
              </div>
              <h4 className="text-success mb-3">
                <FiCheck style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                VPN Request Submitted Successfully!
              </h4>
              <p className="text-muted mb-4">
                Your VPN access request has been submitted and will be reviewed by the IT team.
              </p>
              <p className="text-muted mb-4">
                You will receive a confirmation email shortly at <strong>{formData.email}</strong>
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="vpn-form-container">
      <div className="vpn-form-wrapper">
        {/* Header Section */}
        <div className="vpn-form-header">
          <h1 className="vpn-form-title">
            <FiShield style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
            Island Hospital VPN Access Review Form
          </h1>
          <p className="vpn-form-subtitle">
            Please fill out all required fields to request Island Hospital VPN access for your device
          </p>
        </div>

        {/* Main Form Card */}
        <Card>
          <CardHeader>
            <CardTitle as='h5' className="mb-0">
              <FiUser style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
              Vendor VPN Request Information
            </CardTitle>
          </CardHeader>
          <CardBody>
            {/* Expiration Reminder (Malaysia time) */}
            {isRenewalForm && expirationDate && !isExpired && (
              <Alert variant="warning" className="mb-4">
                <div className="d-flex align-items-start">
                  <FiAlertTriangle style={{ fontSize: '1.2rem', marginTop: '2px', marginRight: '0.5rem', flexShrink: 0 }} />
                  <div>
                    <h6 className="mb-2 fw-bold">⚠️ Important: 14-Day Deadline</h6>
                    <p className="mb-2">
                      <strong>Please fill in this form within 14 days.</strong> This form will expire on <strong>{formatMalaysiaDateTime(expirationDate)}</strong> (Malaysia time).
                    </p>
                    <p className="mb-0">
                      <strong>Important Notice:</strong> If we do not receive your response within 14 days, your VPN account will be automatically disabled. 
                      Please note that reactivation will take approximately 3 days to process once the form is submitted after the deadline.
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              {/* Company Information Section */}
              <div className="form-section-divider">
                <h6 className="section-title">
                  <FaBuildingColumns  style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                  Company Information
                </h6>
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      placeholder="Enter company name"
                      isInvalid={!!errors.companyName}
                      required
                      disabled={isRenewalForm}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.companyName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Contact Person Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="contactPersonName"
                      value={formData.contactPersonName}
                      onChange={handleInputChange}
                      placeholder="Enter contact person's name"
                      isInvalid={!!errors.contactPersonName}
                      required
                      disabled={isRenewalForm}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.contactPersonName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="contact@company.com"
                      isInvalid={!!errors.email}
                      required
                      disabled={isRenewalForm}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      isInvalid={!!errors.phoneNumber}
                      disabled={isRenewalForm}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phoneNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              {/* VPN Access Details Section */}
              <div className="form-section-divider">
                <h6 className="section-title">
                  <FiLock style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                  VPN Access Details
                </h6>
              </div>

              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Reason for Access <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="reasonForAccess"
                      value={formData.reasonForAccess}
                      onChange={handleInputChange}
                      placeholder="Please provide a detailed reason for VPN access..."
                      isInvalid={!!errors.reasonForAccess}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.reasonForAccess}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      isInvalid={!!errors.startDate}
                      required
                      disabled={useUntilCutoff}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.startDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      End Date <span className="text-danger">*</span>
                      <FiInfo 
                        style={{ cursor: 'help', fontSize: '0.9rem', marginLeft: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }}
                        title="VPN access cutoff date is October 31st. If renewing before November, you can select until October 31st of the current year. If renewing in November or later, you can select until October 31st of the next year."
                      />
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      isInvalid={!!errors.endDate}
                      max={formData.startDate ? getMaxEndDate(formData.startDate) : ''}
                      min={formData.startDate || ''}
                      required
                      disabled={useUntilCutoff}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.endDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="useUntilCutoff"
                      checked={useUntilCutoff}
                      onChange={handleUntilCutoffChange}
                      label="Until End of next cut off date (October 31st)"
                    />
                  </Form.Group>
                </Col>

                {duration && (
                  <Col md={12}>
                    <Alert variant="info" className="mb-0">
                      <FiCalendar style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                      <strong>Duration Selected:</strong> {duration.months} month{duration.months !== 1 ? 's' : ''} 
                      {duration.days > 0 && `, ${duration.days} day${duration.days !== 1 ? 's' : ''}`}
                      {' '}({duration.totalDays} total days)
                    </Alert>
                  </Col>
                )}
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Description (OPTIONAL)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Provide additional project or scope details..."
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {/* People That Need VPN Access Section */}
              <div className="form-section-divider">
                <h6 className="section-title">
                  <FiUsers style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                  People That Need VPN Access
                </h6>
              </div>

              <Row className="g-3">
                <Col md={12}>
                  {formData.people.map((person, index) => (
                    <div key={index} className="mb-3">
                      <Row className="g-2">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>
                              Username
                              <FiInfo 
                                style={{ cursor: 'help', fontSize: '0.9rem', marginLeft: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }}
                                title="Username field is managed by admin and cannot be edited"
                              />
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={person.name}
                              placeholder="N/A"
                              disabled
                              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                              (Read-only)
                            </Form.Text>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>
                              Full Name {index === 0 && <span className="text-danger">*</span>}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={person.fullName}
                              onChange={(e) => handlePeopleChange(index, 'fullName', e.target.value)}
                              placeholder="Enter person's full name"
                              isInvalid={!!errors[`people_fullName_${index}`]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors[`people_fullName_${index}`]}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>
                              Email {index === 0 && <span className="text-danger">*</span>}
                            </Form.Label>
                            <Form.Control
                              type="email"
                              value={person.email}
                              onChange={(e) => handlePeopleChange(index, 'email', e.target.value)}
                              placeholder="person@company.com"
                              isInvalid={!!errors[`people_email_${index}`]}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors[`people_email_${index}`]}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col md={1}>
                          <div className="d-flex gap-2 align-items-start">
                            {formData.people.length < 30 && index === formData.people.length - 1 && (
                              <Button
                                variant="primary"
                                onClick={handleAddPerson}
                                type="button"
                                className="mt-4 vpn-person-action-btn vpn-add-btn"
                                title="Add another person"
                              >
                                <FiPlus />
                              </Button>
                            )}
                            {formData.people.length > 1 && (
                              <Button
                                variant="danger"
                                onClick={() => handleRemovePerson(index)}
                                type="button"
                                className="mt-4 vpn-person-action-btn vpn-remove-btn"
                                title="Remove this person"
                              >
                                <FiMinus />
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Form.Text className="text-muted">
                    Minimum 1 person, maximum 30 people. {errors.people && <span className="text-danger">{errors.people}</span>}
                  </Form.Text>
                </Col>
              </Row>

              {/* Security Compliance */}
              <div className="form-section-divider">
                <h6 className="section-title">
                  <FiShield style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                  Security Compliance
                </h6>
              </div>

              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      name="securityCompliance"
                      checked={formData.securityCompliance}
                      onChange={handleInputChange}
                      isInvalid={!!errors.securityCompliance}
                      label={
                        <span>
                          I confirm that I have read and understand the{' '}
                          <a 
                            href={`${process.env.REACT_APP_AI_FORM_SERVER || 'http://localhost:4454'}/api/vpn-security-policy`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#0d6efd', textDecoration: 'underline' }}
                          >
                            Company's VPN Security Policy
                          </a>
                          {' '}and will comply with all security requirements
                        </span>
                      }
                    />
                    {errors.securityCompliance && (
                      <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {errors.securityCompliance}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              {/* Submit Error */}
              {errors.submit && (
                <Alert variant="danger" className="mt-3">
                  {errors.submit}
                </Alert>
              )}

              {/* Form Actions */}
              <Row className="mt-4">
                <Col md={12}>
                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting || isExpired}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting Request...
                        </>
                      ) : (
                        <>
                          <FiSend style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
                          Submit Request
                        </>
                      )}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        {/* Footer Information */}
        <div className="vpn-form-footer">
          <p className="text-muted">
            <FiInfo style={{ marginRight: '0.5rem', display: 'inline-block', verticalAlign: 'middle' }} />
            For assistance, please contact IT Support at <strong>ihp-infra@islandhospital.com</strong> 
          </p>
        </div>
      </div>
    </div>
  );
};

export default VPNDigitalForm;

